import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export interface FAQItem { question: string; answer: string; }
export interface FAQSectionProps { headline: string; subheadline?: string; faqs: FAQItem[]; }

export default function FAQSection({ headline, subheadline, faqs }: FAQSectionProps) {
  const [open, setOpen] = useState<number | null>(null);
  const { ref, visible } = useScrollAnimation();

  return (
    <section className="bg-jc-cream py-24 md:py-36 overflow-hidden">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <div
          ref={ref}
          className="text-center mb-14"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-6 h-px bg-jc-gold"></span>
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-jc-gold">Voice Search Ready</span>
            <span className="w-6 h-px bg-jc-gold"></span>
          </div>
          <h2 className="font-serif text-[clamp(2rem,4vw,3.2rem)] font-light text-jc-charcoal leading-[1.12]">{headline}</h2>
          {subheadline && <p className="text-gray-400 text-[0.95rem] mt-5 max-w-lg mx-auto leading-relaxed">{subheadline}</p>}
        </div>

        <div className="space-y-2.5">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="bg-white border border-transparent rounded-xl overflow-hidden transition-all duration-300"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(16px)',
                transition: `opacity 0.5s ease ${i * 0.06}s, transform 0.5s ease ${i * 0.06}s, border-color 0.3s ease`,
                borderColor: open === i ? 'rgba(201,169,97,0.4)' : 'transparent',
              }}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between px-6 py-5 text-left group cursor-pointer"
              >
                <span className="font-medium text-jc-charcoal text-[0.92rem] pr-6 leading-snug group-hover:text-jc-gold transition-colors duration-200">
                  {faq.question}
                </span>
                <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${open === i ? 'bg-jc-gold' : 'bg-jc-cream'}`}>
                  <i className={`text-sm transition-transform duration-300 ${open === i ? 'ri-subtract-line text-jc-charcoal' : 'ri-add-line text-jc-charcoal'}`}></i>
                </div>
              </button>
              <div
                className="overflow-hidden transition-all duration-300"
                style={{ maxHeight: open === i ? '200px' : '0' }}
              >
                <div className="px-6 pb-6">
                  <div className="h-px bg-gray-100 mb-4"></div>
                  <p className="text-gray-500 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
